import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDeleteMenuSection, useMenuSections, useUpdateMenuSection } from "@/hooks/use-menu";
import { useActiveOrganization } from "@/hooks/use-organizations";

const formatDate = (value: string) => {
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(value));
};

export default function MenuSectionsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: activeOrganization, isLoading: isLoadingOrganization } = useActiveOrganization();
  const { data, error, isLoading } = useMenuSections(activeOrganization?.id);
  const updateSection = useUpdateMenuSection();
  const deleteSection = useDeleteMenuSection();

  const getParentName = (parentId: string | null) => {
    if (!(parentId && data?.sections)) {
      return null;
    }
    return data.sections.find((section) => section.id === parentId)?.name ?? null;
  };

  const handleDelete = (sectionId: string) => {
    deleteSection.mutate(sectionId, {
      onError: (mutationError) => {
        toast.error(mutationError.message);
      },
      onSuccess: () => {
        toast.success(t("menu.sections.notifications.deleted"));
      },
    });
  };

  const handleVisibilityChange = (sectionId: string, visible: boolean) => {
    updateSection.mutate(
      {
        data: { visible },
        id: sectionId,
      },
      {
        onError: (mutationError) => {
          toast.error(mutationError.message);
        },
      },
    );
  };

  if (isLoadingOrganization) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!activeOrganization) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("menu.sections.title")}</CardTitle>
            <CardDescription>{t("menu.sections.notifications.activeOrgRequired")}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const isSupermarket = activeOrganization.businessType === "supermarket";
  const copy = {
    description: isSupermarket
      ? t("menu.sections.catalog.description", {
          defaultValue: "Gestiona las categorías y subcategorías de {{organization}}.",
          organization: activeOrganization.name,
        })
      : t("menu.sections.description", {
          organization: activeOrganization.name,
        }),
    emptyDescription: isSupermarket
      ? t("menu.sections.catalog.table.emptyDescription", {
          defaultValue: "Crea tu primera categoría para empezar a construir el catálogo.",
        })
      : t("menu.sections.table.emptyDescription"),
    emptyTitle: isSupermarket
      ? t("menu.sections.catalog.table.emptyTitle", {
          defaultValue: "Todavía no hay categorías",
        })
      : t("menu.sections.table.emptyTitle"),
    newButton: isSupermarket
      ? t("menu.sections.catalog.newButton", {
          defaultValue: "Nueva categoría",
        })
      : t("menu.sections.newButton"),
    parentLabel: isSupermarket
      ? t("menu.sections.catalog.table.parentLabel", {
          defaultValue: "Categoría padre",
        })
      : t("menu.sections.table.parentLabel", { defaultValue: "Parent" }),
    tableDescription: isSupermarket
      ? t("menu.sections.catalog.table.description", {
          defaultValue:
            "Revisa, edita, oculta o elimina las categorías y subcategorías de este catálogo.",
        })
      : t("menu.sections.table.description"),
    tableTitle: isSupermarket
      ? t("menu.sections.catalog.table.title", {
          defaultValue: "Categorías actuales",
        })
      : t("menu.sections.table.title"),
    title: isSupermarket
      ? t("menu.sections.catalog.title", { defaultValue: "Categorías" })
      : t("menu.sections.title"),
  };

  let sectionsContent: ReactNode = (
    <div className="rounded-lg border border-dashed p-8 text-center">
      <h2 className="font-medium text-base">{copy.emptyTitle}</h2>
      <p className="mt-2 text-muted-foreground text-sm">{copy.emptyDescription}</p>
    </div>
  );

  if (isLoading) {
    sectionsContent = (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  } else if (error) {
    sectionsContent = <p className="text-destructive text-sm">{error.message}</p>;
  } else if (data?.sections.length) {
    sectionsContent = (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("menu.sections.table.name")}</TableHead>
            <TableHead>{t("menu.sections.table.slug")}</TableHead>
            <TableHead>{t("menu.sections.table.visibility")}</TableHead>
            <TableHead>{t("menu.sections.table.updated")}</TableHead>
            <TableHead className="text-right">{t("menu.sections.table.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.sections.map((section) => (
            <TableRow key={section.id}>
              <TableCell>
                <div className="space-y-1">
                  <p className="font-medium">
                    {section.parentId ? "-- " : ""}
                    {section.name}
                  </p>
                  {section.parentId ? (
                    <p className="text-muted-foreground text-xs">
                      {copy.parentLabel}: {getParentName(section.parentId) ?? "-"}
                    </p>
                  ) : null}
                  {section.description ? (
                    <p className="text-muted-foreground text-xs">{section.description}</p>
                  ) : null}
                </div>
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">/{section.slug}</TableCell>
              <TableCell>
                <Switch
                  checked={section.visible}
                  onCheckedChange={(checked) => handleVisibilityChange(section.id, checked)}
                />
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {formatDate(section.updatedAt)}
              </TableCell>
              <TableCell>
                <div className="flex justify-end gap-2">
                  <Button
                    onClick={() => navigate(`/menu/sections/${section.id}/edit`)}
                    size="icon"
                    type="button"
                    variant="outline"
                  >
                    <IconEdit className="size-4" />
                    <span className="sr-only">{t("menu.sections.actions.edit")}</span>
                  </Button>
                  <Button
                    onClick={() => handleDelete(section.id)}
                    size="icon"
                    type="button"
                    variant="outline"
                  >
                    <IconTrash className="size-4" />
                    <span className="sr-only">{t("menu.sections.actions.delete")}</span>
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <div className="flex flex-col gap-8 p-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="font-serif font-semibold text-2xl tracking-tight">{copy.title}</h1>
          <p className="text-muted-foreground text-sm">{copy.description}</p>
        </div>
        <Button onClick={() => navigate("/menu/sections/new")} type="button">
          <IconPlus className="mr-2 size-4" />
          {copy.newButton}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-serif">{copy.tableTitle}</CardTitle>
          <CardDescription>{copy.tableDescription}</CardDescription>
        </CardHeader>
        <CardContent>{sectionsContent}</CardContent>
      </Card>
    </div>
  );
}
